import React, { useEffect, useMemo, useState } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { getVMTDataForGHG } from '../../services/vmtDataService';
import './VMTOverTimeChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface VMTOverTimeChartProps {
  location: string;
  vmtReductionPercent: number;
  geofips?: number | null;
  emfacVersion?: '2021' | '2025';
}

interface EmfacRow {
  year: number;
  forecasting_ratio?: number;
}

const vmtFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

/**
 * Fetch EMFAC forecasting ratios directly from Supabase.
 * Resolves county from the city name so the query matches county-level EMFAC data.
 */
async function fetchEmfacRatios(
  location: string,
  emfacVersion: '2021' | '2025'
): Promise<EmfacRow[]> {
  try {
    await supabaseLocationService.initialize();
  } catch {
    // may already be initialised
  }

  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const tableName =
    emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac2021_county_filtered';

  const locationVariations = [
    `${baseCountyName} County`,
    baseCountyName,
    resolvedCounty,
  ];

  for (const loc of locationVariations) {
    const { data, error } = await supabaseClient
      .from(tableName)
      .select('year, forecasting_ratio')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });

    if (!error && data && data.length > 0) {
      return data as EmfacRow[];
    }
  }

  return [];
}

/**
 * Build BAU and policy VMT series from baseline (2023 forward).
 * BAU uses EMFAC forecasting ratios to project VMT from 2023 to 2045.
 * Policy applies a gradual VMT reduction on top of the BAU path.
 */
function buildProjectionSeries(
  baselineVmt2023: number,
  emfacRows: EmfacRow[],
  vmtReductionPercent: number
): { years: number[]; bau: number[]; policy: number[] } {
  const years: number[] = [];
  const bau: number[] = [];
  const policy: number[] = [];

  const byYear = new Map<number, number>();
  emfacRows.forEach((r) => {
    const y = Number(r?.year);
    if (
      Number.isFinite(y) &&
      typeof r?.forecasting_ratio === 'number' &&
      r.forecasting_ratio > 0
    ) {
      byYear.set(y, r.forecasting_ratio);
    }
  });
  const sortedYears = Array.from(byYear.keys()).sort((a, b) => a - b);

  const getRatio = (y: number): number => {
    const exact = byYear.get(y);
    if (typeof exact === 'number' && exact > 0) return exact;
    if (sortedYears.length === 0) return 1;
    let lo: number | undefined;
    let hi: number | undefined;
    for (const yy of sortedYears) {
      if (yy < y) lo = yy;
      if (yy > y) {
        hi = yy;
        break;
      }
    }
    const loVal = lo !== undefined ? byYear.get(lo) : undefined;
    const hiVal = hi !== undefined ? byYear.get(hi) : undefined;
    if (
      lo !== undefined &&
      hi !== undefined &&
      lo !== hi &&
      typeof loVal === 'number' &&
      typeof hiVal === 'number'
    ) {
      const t = (y - lo) / (hi - lo);
      return loVal + (hiVal - loVal) * t;
    }
    const last = sortedYears[sortedYears.length - 1];
    const lastVal = byYear.get(last);
    return typeof lastVal === 'number' && lastVal > 0 ? lastVal : 1;
  };

  const totalSteps = 2045 - 2023;
  let currentBau = baselineVmt2023;
  years.push(2023);
  bau.push(baselineVmt2023);
  policy.push(baselineVmt2023);

  for (let y = 2024; y <= 2045; y++) {
    const ratio = getRatio(y);
    currentBau = currentBau * ratio;

    const yearsElapsed = y - 2023;
    const annualVmtr = vmtReductionPercent / totalSteps;
    const vmtReductionPct = Math.min(vmtReductionPercent, annualVmtr * yearsElapsed);
    const policyVmt = currentBau * (1 - vmtReductionPct / 100);

    years.push(y);
    bau.push(currentBau);
    policy.push(policyVmt);
  }

  return { years, bau, policy };
}

const VMTOverTimeChart: React.FC<VMTOverTimeChartProps> = ({
  location,
  vmtReductionPercent,
  geofips = null,
  emfacVersion = '2025',
}) => {
  const [historicalVmt, setHistoricalVmt] = useState<Array<{ year: number; vmt: number }>>([]);
  const [emfacData, setEmfacData] = useState<EmfacRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch historical VMT and EMFAC forecasting ratios in parallel
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      getVMTDataForGHG(location, geofips ?? undefined, emfacVersion),
      fetchEmfacRatios(location, emfacVersion),
    ])
      .then(([vmtRaw, emfac]) => {
        if (cancelled) return;
        const points = vmtRaw
          .filter((d) => Number.isFinite(d.year) && Number.isFinite(d.vmt))
          .map((d) => ({ year: d.year, vmt: d.vmt || 0 }))
          .sort((a, b) => a.year - b.year);
        setHistoricalVmt(points);
        setEmfacData(emfac);
      })
      .catch(() => {
        if (!cancelled) {
          setHistoricalVmt([]);
          setEmfacData([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location, geofips, emfacVersion]);

  const chartData = useMemo(() => {
    // Use the most recent historical VMT value as the 2023 baseline
    const hist2023 = historicalVmt.find((d) => d.year === 2023);
    const baselineVmt2023 = hist2023
      ? hist2023.vmt
      : historicalVmt.length > 0
        ? historicalVmt[historicalVmt.length - 1].vmt
        : 0;

    const projection =
      baselineVmt2023 > 0 && emfacData.length > 0
        ? buildProjectionSeries(baselineVmt2023, emfacData, vmtReductionPercent)
        : null;

    const allYears = new Set<number>();
    historicalVmt.forEach((d) => allYears.add(d.year));
    if (projection) projection.years.forEach((y) => allYears.add(y));
    const years = Array.from(allYears).sort((a, b) => a - b);
    if (years.length === 0) return null;

    const histByYear = new Map(historicalVmt.map((d) => [d.year, d.vmt]));

    const historicalValues = years.map((y) => {
      const v = histByYear.get(y);
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    });

    const bauValues = years.map((y) => {
      if (!projection) return null;
      const idx = projection.years.indexOf(y);
      return idx >= 0 ? projection.bau[idx] : null;
    });

    const policyValues = years.map((y) => {
      if (!projection) return null;
      const idx = projection.years.indexOf(y);
      return idx >= 0 ? projection.policy[idx] : null;
    });

    return {
      labels: years.map(String),
      datasets: [
        {
          label: 'Historical / observed',
          data: historicalValues,
          borderColor: 'rgb(107, 114, 128)',
          backgroundColor: 'rgba(107, 114, 128, 0.1)',
          borderWidth: 2,
          tension: 0.2,
          pointRadius: years.length <= 25 ? 3 : 0,
          spanGaps: false,
        },
        {
          label: `Business as usual (EMFAC ${emfacVersion})`,
          data: bauValues,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          tension: 0.2,
          pointRadius: years.length <= 25 ? 3 : 0,
          borderDash: [],
          spanGaps: false,
        },
        {
          label: 'With VMT reduction policy',
          data: policyValues,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          tension: 0.2,
          pointRadius: years.length <= 25 ? 3 : 0,
          borderDash: [8, 4],
          spanGaps: false,
        },
      ],
    };
  }, [emfacVersion, historicalVmt, emfacData, vmtReductionPercent]);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `Vehicle Miles Traveled Over Time (EMFAC ${emfacVersion})`,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const v = context.parsed.y;
              if (v == null) return '';
              return `${context.dataset.label}: ${vmtFormatter.format(v)} miles`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year',
          },
          grid: {
            color: (ctx) =>
              ctx.tick?.label === '2023' ? 'rgba(107, 114, 128, 0.5)' : 'rgba(0, 0, 0, 0.06)',
          },
        },
        y: {
          title: {
            display: true,
            text: 'Total VMT (miles)',
          },
          beginAtZero: true,
          ticks: {
            callback: (value) =>
              typeof value === 'number' && value >= 1e6
                ? `${(value / 1e6).toFixed(1)}M`
                : value,
          },
        },
      },
    }),
    [emfacVersion]
  );

  if (loading && historicalVmt.length === 0) {
    return (
      <div className="vmt-over-time-chart vmt-over-time-chart-loading">
        Loading VMT data…
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="vmt-over-time-chart vmt-over-time-chart-empty">
        No VMT or EMFAC data available for this location.
      </div>
    );
  }

  return (
    <div className="vmt-over-time-chart">
      <div className="vmt-over-time-chart-inner">
        <Line data={chartData} options={options} />
      </div>
      <p className="vmt-over-time-chart-note">
        Historical/observed VMT uses available data. Future projections use EMFAC{emfacVersion} forecasting ratios.
        The "with policy" line applies the VMT reduction slider as a gradual ramp from 2023 to 2045.
      </p>
    </div>
  );
};

export default VMTOverTimeChart;
